import React, { useCallback, useState } from 'react';
import { Modal, Button, message } from 'antd';

import { ReactComponent as CrossSvg } from '@assets/icons/cross.svg';
import { useTypedSelector } from '@hooks';
import { toggleEntityDeletionModal } from '@redux/actions/modalAction';
import {
  deleteAssetAction,
  deleteFolderAction
} from '@redux/actions/assetsAction';
import { removeCampaign } from '@redux/actions/campaignAction';
import { AssetItemDto, CampaignItemDto } from '@api/Api';
import { MixpanelEventType, MixpanelService } from '@services/mixpanelService';
import { useAuth } from '@hooks/useAuth';
import { htmlSanitize } from '@helpers/htmlSanitize';
import useTypedDispatch from '@hooks/useTypedDispatch';

function DeleteEntityModal() {
  const dispatch = useTypedDispatch();
  const { user } = useAuth(true, 'internal');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const entityDeletionModal = useTypedSelector(
    ({ modal }) => modal.entityDeletionModal
  );

  const onFinish = async () => {
    try {
      setIsSubmitting(true);
      if (entityDeletionModal?.page === 'campaign') {
        const campaign = entityDeletionModal.entity as CampaignItemDto;
        await dispatch(
          removeCampaign({
            campaignId: entityDeletionModal.entity.id,
            permanent: entityDeletionModal?.permanent || false
          })
        );
        MixpanelService.track(user.id, MixpanelEventType.MANAGE_CAMPAIGNS, {
          campaignName: campaign.name,
          deleteCampaign: true,
          newCampaign: false,
          privateCampaign: campaign.isPrivate,
          sharedCampaign: !campaign.isPrivate,
          addTaskGroup: false,
          addContributor: false
        });
      }
      if (entityDeletionModal?.page === 'folder') {
        await dispatch(
          deleteFolderAction({
            folderId: entityDeletionModal.entity.id,
            permanent: entityDeletionModal?.permanent || false
          })
        );
      }
      if (entityDeletionModal?.page === 'asset') {
        const asset = entityDeletionModal.entity as AssetItemDto;
        await dispatch(
          deleteAssetAction(
            {
              assetId: asset.id || '',
              permanent: entityDeletionModal?.permanent || false
            },
            asset.path?.[0]?.id
          )
        );

        MixpanelService.track(user.id, MixpanelEventType.MANAGE_ASSETS, {
          addToFavorite: false,
          deleteAsset: true,
          newAsset: false,
          externalLinkShare: false,
          approvalRouting: false,
          newCommentAsset: false,
          annotationLeft: false,
          taskFromComment: false
        });
      }

      setIsSubmitting(false);
      dispatch(toggleEntityDeletionModal(null));
      if (entityDeletionModal?.func) entityDeletionModal.func();
    } catch (err: any) {
      message.error(
        err?.response?.data?.message || err?.message || 'Something went wrong'
      );
      setIsSubmitting(false);
    }
  };
  const copies = useCallback(() => {
    if (entityDeletionModal?.page === 'folder') {
      return {
        title: 'Delete folder',
        subtitle: 'Do you want to delete this folder and all media inside?'
      };
    }

    if (entityDeletionModal?.page === 'asset') {
      const entity = entityDeletionModal?.entity as AssetItemDto;
      if (entity?.asset?.versions && Array.isArray(entity.asset.versions)) {
        const isActiveAW =
          entity.asset.approvalWorkflow &&
          !entity.asset.approvalWorkflow?.isFinished;
        if (isActiveAW) {
          const version = entity.asset.versions[0];
          return {
            title: 'Delete media?',
            subtitle: `<strong>${version.name}.${version.extension}</strong> has an approval routing in progress. Do you want to delete the media?`,
            btn: 'Yes, delete media'
          };
        }
      }
      return {
        title: 'Delete media',
        subtitle: `Are you sure you want to delete media and all (${entity.asset?.versionsCount}) versions?`
      };
    }

    if (entityDeletionModal?.page === 'campaign') {
      return {
        title: 'Delete project',
        subtitle: `Are you sure you want to delete this project and all media and tasks inside?`
      };
    }
  }, [entityDeletionModal])();
  return (
    <Modal
      visible={entityDeletionModal?.visible}
      footer={null}
      centered={true}
      closeIcon={<CrossSvg />}
      width={500}
      destroyOnClose
      zIndex={1010}
      onCancel={() => dispatch(toggleEntityDeletionModal(null))}
    >
      <div className="modal_container">
        <h2
          className="modal_container__title"
          style={{ fontSize: 22, marginBottom: '0.5em' }}
        >
          {copies?.title} {entityDeletionModal?.permanent && 'forever'}
        </h2>

        <p
          className="modal_container__subtitle"
          style={{ marginBottom: '32px' }}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: htmlSanitize(copies?.subtitle || '') as any
          }}
        />
        <div className="modal_container__actions">
          <Button
            type="text"
            size="large"
            disabled={isSubmitting}
            style={{
              marginRight: '16px',
              border: 'none',
              height: 48,
              padding: '8px 32px'
            }}
            onClick={() => dispatch(toggleEntityDeletionModal(null))}
          >
            No
          </Button>
          <Button
            type="primary"
            size="large"
            onClick={onFinish}
            loading={isSubmitting}
            style={{ minWidth: 124, height: 48, padding: '8px 15px' }}
          >
            {copies?.btn || 'Yes'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default DeleteEntityModal;
